'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import * as Sentry from '@sentry/react';

import Alert from '@components/Widgets/Alert/Alert.react';
import ProgressBar from '@components/Widgets/ProgressBar.react';
import TextArea from '@components/Input/TextArea.react';

import AuthStore from '@stores/AuthStore';
import { getConfig } from '@utils/Env';
import Analytics from '@utils/Analytics';
import { flattenIngredients } from '@utils/Recipe';
import { getRecipeEventProperties } from '@utils/Content';

import './QuickAddIngredients.scss';

// The import job has no real progress signal, so we fake a progress bar off its status:
// creep 0->10% while queued, 10->90% while processing, snap to 100% on completion.
const PROGRESS_QUEUED_CEILING = 10;
const PROGRESS_PROCESSING_CEILING = 90;
const PROGRESS_TICK_MS = 250;

export default class QuickLogAiModal extends Component {
    static propTypes = {
        mealType: PropTypes.string,
        date: PropTypes.object,
        onLogFoods: PropTypes.func,
    };

    static contextTypes = {
        editSessionId: PropTypes.string,
    };

    constructor(props) {
        super(props);

        this.state = {
            input: '',
            showModal: props.showModal || false,
            loading: false,
            progress: 0,
            jobStatus: null,
        };
    }

    componentWillUnmount() {
        this.stopProgress();
    }

    openModal = () => {

        this.setState({ showModal: true, progress: 0, jobStatus: null });

        const { mealType, date } = this.props;
        Analytics.openQuickLogAI({
            'Meal Type': mealType,
            'Date': date ? date.format('YYYY-MM-DD') : null,
        });
    };

    closeModal = () => {
        this.stopProgress();
        this.setState({ showModal: false, input: '', error: null, loading: false, delay: false, progress: 0, jobStatus: null });

        const { mealType, date } = this.props;

        if (!this.state.loading) {
            Analytics.closeQuickLogAI({
                'Meal Type': mealType,
                'Date': date ? date.format('YYYY-MM-DD') : null,
            });
        }
    };

    onChange = (event) => {
        this.setState({ input: event.target.value });
    };

    // Target the bar should creep toward for the current job status.
    progressCeilingFor = (status) => {
        if (status === 'completed') {
            return 100;
        }
        if (status === 'queued') {
            return PROGRESS_QUEUED_CEILING;
        }
        // processing, or any other non-terminal status the backend may report
        return PROGRESS_PROCESSING_CEILING;
    };

    // Ease the bar toward the current status ceiling, holding there until the status advances.
    startProgress = () => {
        this.stopProgress();
        this.setState({ progress: 0, jobStatus: 'queued' });

        this.progressTimer = setInterval(() => {
            this.setState((prev) => {
                const ceiling = this.progressCeilingFor(prev.jobStatus);
                if (prev.progress >= ceiling) {
                    return null;
                }
                const step = Math.max(0.4, (ceiling - prev.progress) * 0.08);
                return { progress: Math.min(prev.progress + step, ceiling) };
            });
        }, PROGRESS_TICK_MS);
    };

    stopProgress = () => {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
    };

    importRecipe = async () => {
        let { input } = this.state;
        const { onLogFoods } = this.props;
        const { editSessionId } = this.context;

        input = input.trim();

        if (!input) {
            this.setState({ error: 'Please describe your meal by typing it into the text field above.' });
            return;
        }

        this.setState({ loading: true, error: false, delay: false });
        this.startProgress();
        let timeoutId = setTimeout(() => this.setState({ delay: true }), 30000);

        const payload = { text: input, import_context: 'quick log' };

        const start = performance.now();

        // Wrapped in try/catch/finally so a thrown fetch/network error can never leave the modal
        // stuck on the loading spinner.
        try {
            const response = await AuthStore.fetch(getConfig('recipe_api') + '/import-recipe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; schema=import/recipe/text/1' },
                body: JSON.stringify(payload),
            });

            if (!response?.links.self.href) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: response?.error,
                    'Edit Session ID': editSessionId,
                });

                this.setState({
                    loading: false,
                    error: response?.error || 'unknown error',
                    delay: false,
                });
                return;
            }

            Analytics.startQuickLogAI({
                ...payload,
                'Job UUID': response?.uuid,
                'Edit Session ID': editSessionId,
            });

            let jobUrl = getConfig('recipe_api') + response?.links.self.href;
            let job = await AuthStore.fetch(jobUrl);
            let governor = 500;
            this.setState({ jobStatus: job?.status });

            while (job?.status !== 'completed' && job?.status !== 'failed' && governor-- > 0) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                job = await AuthStore.fetch(jobUrl);
                this.setState({ jobStatus: job?.status });

                if (!this.state.showModal) {
                    // user closed the modal, abort and ignore.
                    Analytics.cancelQuickLogAI({
                        ...payload,
                        'Job ID': job.uuid,
                    });
                    return;
                }
            }

            if (job?.status !== 'completed' || job?.output?.error) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: job.output?.error,
                    'Job ID': job.uuid,
                    'Edit Session ID': editSessionId,
                });

                this.setState({
                    loading: false,
                    error: job?.output?.error || 'unknown error',
                    delay: false,
                });
                return;
            }

            const { recipe, details: recipeDetails } = job?.output;

            const ingredients = recipeDetails?.ingredients?.flatMap((ingredient) => ingredient.items) || [];

            if (!ingredients.length) {
                Analytics.quickLogAIError({
                    ...payload,
                    error: 'No foods returned',
                    'Job ID': job.uuid,
                    'Edit Session ID': editSessionId,
                });
                this.setState({
                    loading: false,
                    error: 'Unable to recognize food items. Please provide more details and try again.',
                    delay: false,
                });
                return;
            }

            // Job is done — fill the bar to 100% (CSS smooths the final jump) before we hand off.
            this.stopProgress();
            this.setState({ progress: 100, jobStatus: 'completed' });

            await onLogFoods(recipeDetails, job);

            const end = performance.now();
            const durationSeconds = (end - start) / 1000;
            const roundedDuration = Math.round(durationSeconds * 100) / 100;

            Analytics.quickLogAISuccess({
                ...payload,
                ...getRecipeEventProperties(recipe, recipeDetails),
                'Duration Seconds': roundedDuration,
                'Job ID': job.uuid,
                'Edit Session ID': editSessionId,
            });

            this.setState({ showModal: false, loading: false, error: false, delay: false, input: '' });
        } catch (error) {
            // A thrown fetch/network error (or unexpected exception) — surface it and reset loading
            // so the modal doesn't spin forever.
            Analytics.quickLogAIError({
                ...payload,
                error: error?.message || 'unexpected error',
                'Edit Session ID': editSessionId,
            });

            this.setState({ loading: false, error: 'unknown error', delay: false });
        } finally {
            this.stopProgress();
            clearTimeout(timeoutId);
        }
    };

    renderLoader = () => {
        const { loading } = this.state;

        if (!loading) {
            return;
        }

        return (
            <div className="ingredients-loading">
                <i className="icon-spinner2" />
            </div>
        );
    };

    getErrorForDisplay = () => {
        const { error } = this.state;

        if (!error) {
            return;
        }

        if (error === 'Unable to import: Raw nutrition information is not supported at this time') {
            return <>Please enter the food and amount (e.g., '1 apple'), instead of nutritional values.</>
        } else if (error === 'Unable to import: Questions are not supported at this time') {
            return <>Please enter the food and amount (e.g., '1 apple')</>
        } else if (error === 'Unable to import: Unable to extract a food log or recipe from this text/plain') {
            return <>Unable to recognize the food or amount. Please enter the food and amount clearly (e.g., '1 apple' or '100g chicken').</>
        } else if (error === 'Unable to import: Can\'t extract a food log or recipe from this document.') {
            return <>Unable to recognize the food or amount. Please enter the food and amount clearly (e.g., '1 apple' or '100g chicken').</>
        } else if (error === 'Unable to import: QuickLog AI is currently unavailable') {
            Sentry.withScope(scope => {
                const sentryError = new Error(error);
                sentryError.name = "QuickLogAIUnavailableError";
                Sentry.captureException(sentryError);
            });
            return (
            <>
                We're sorry. QuickLog AI is currently unavailable. We apologize for the inconvenience and are working quickly to restore service.
                Please try again in a little while. For assistance, contact support@eatlove.is.
            </>
            );
        }

        return <>Unable to recognize food items. Please provide more details and try again.</>;
    }

    renderModal = () => {
        const { showModal, input, loading, delay, progress } = this.state;

        if (!showModal) {
            return;
        }

        const error = this.getErrorForDisplay();

        return (
            <Modal
                isOpen={true}
                onRequestClose={this.closeModal}
                contentLabel="Describe Your Meal"
                className={`el-modal el-modal3 el-modal3-centered`}
                overlayClassName={`el-modal-overlay`}
                closeTimeoutMS={250}
            >
                <div className="el-modal-container el-modal3-container">
                    {loading ? (
                        <div className="el-modal-loading el-fonts ingredients-loader" data-testid="ingredients-loader">
                            <h4>Please wait</h4>
                            <ProgressBar percentage={progress} className="quicklog-progress" />
                            <span className="loading-text">
                                Analyzing your meal to create log entries... {Math.round(progress)}%
                            </span>
                        </div>
                    ) : (
                        <>
                            <header>
                                <h2>Describe Your Meal</h2>
                                <button className="el-modal-close-x" onClick={this.closeModal}>
                                    <i className="icon-close-x" />
                                    <span className="assistive-text">Close Modal</span>
                                </button>
                            </header>
                            <div className="el-modal-body-container el-modal3-body-container el-form el-fonts">
                                <TextArea
                                    autoFocus
                                    maxLength={5000}
                                    textareaClassName="quick-add-ingredients-textarea"
                                    placeholder={
                                        'Describe your meal by using text or voice using the microphone button on your keyboard (if available).\n\nExample: 1 cheeseburger, small fries, 1 cup of broccoli, 1 cup of milk'
                                    }
                                    defaultValue={input}
                                    onChange={this.onChange}
                                />
                                {error && <Alert type="error">{error}</Alert>}
                            </div>
                        </>
                    )}

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={this.closeModal}>
                            Cancel
                        </button>
                        <button disabled={loading} className="el-modal-ok-btn" onClick={this.importRecipe}>
                            Log
                        </button>
                    </footer>
                </div>
            </Modal>
        );
    };

    render() {
        return (
            <>
                <button className="add-food-btn quick-log-btn" onClick={this.openModal}>
                    Quick Log <em className="add-food-btn ai-false-btn">AI</em>
                </button>
                {this.renderModal()}
            </>
        );
    }
}
